import React, { useEffect, useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'

import {
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react'

import { Input, Select } from '@/components/Form'

import useUser from '@/features/users/useUser'

//Constant
import { SystemVariableType } from '@/features/systemVariables/constants'
import useSystemVariable from '@/features/systemVariables/useSystemVariable'
import { useApplicationType } from '../../useApplicationType'

const ApplicationTypeDetailModal = ({ modalDetail, setModalDetail }) => {
  // Redux
  const { show, type, isCreate, detail } = modalDetail

  const [userOptions, setUserOptions] = useState([])
  // Hook
  const { useFetchAllUsers } = useUser()
  const { useSystemVariableOptions } = useSystemVariable()

  const { useCreateApplicationType, useUpdateApplicationType } = useApplicationType()

  //TODO: Need to test add application type
  const addApplicationType = useCreateApplicationType()
  const updateApplicationType = useUpdateApplicationType()
  const methods = useForm({ defaultValues: detail })

  const firstApproverType = useWatch({ name: 'firstApproverType', control: methods.control })

  const { data: applicationCategoryOptions } = useSystemVariableOptions(SystemVariableType.APPLICATION_CATEGORY, {
    status: 'active',
  })
  const { data: fetchUsersResponse } = useFetchAllUsers({
    page: 1,
    pageSize: 0,
    sortBy: 'fullName',
    orderBy: 1,
    filters: { status: 'active' },
  })

  useEffect(() => {
    if (detail?.firstApprover?._id) {
      // The detail is populated in backend
      detail.firstApprover = detail.firstApprover._id
    }
    if (detail?.secondApprover?._id) {
      // The detail is populated in backend
      detail.secondApprover = detail.secondApprover._id
    }
    if (detail?.finalApprover?._id) {
      // The detail is populated in backend
      detail.finalApprover = detail.finalApprover._id
    }
    methods.reset(detail)
  }, [detail, methods])

  useEffect(() => {
    if (fetchUsersResponse?.data) {
      const allUsers = fetchUsersResponse?.data

      const allUsersOptions = allUsers.map((user) => ({
        value: user._id,
        label: user.fullName,
      }))
      setUserOptions(allUsersOptions)
    }
  }, [fetchUsersResponse?.data])

  // Handler
  const closeModal = () => {
    setModalDetail({
      show: false,
      type: '',
      isCreate: true,
      detail: {},
    })
  }

  const onSubmit = (data) => {
    if (isCreate) {
      addApplicationType.mutate(
        { type, ...data },
        {
          onSettled: () => {
            closeModal()
          },
        }
      )
    } else {
      const { id } = detail
      const payload = {
        ...data,
      }

      delete payload.id

      if (!id) {
        return
      }

      updateApplicationType.mutate(
        { id, data: payload },
        {
          onSuccess: () => {
            closeModal()
          },
        }
      )
    }
  }

  return (
    <Modal size="xl" isOpen={show} onClose={closeModal}>
      <ModalOverlay />
      <ModalContent>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ModalHeader>{isCreate ? 'Create' : 'Update'} Application Type</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Flex gap={3} direction={'column'}>
                <Input name="name" label="Name" type="text" placeholder="Enter value" isRow />
                <Select name="category" label="Category" options={applicationCategoryOptions} isRow />
                <Input
                  name="validity"
                  label="Validity"
                  type="text"
                  placeholder="Enter value"
                  isRow
                  isRequired={false}
                />
                <Input
                  name="referenceLabel"
                  label="Reference Label"
                  type="text"
                  placeholder="Reference"
                  isRow
                  isRequired={false}
                />
                <Select
                  name="firstApproverType"
                  label="First Approver Type"
                  options={[
                    { value: 'lineManager', label: 'Line Manager' },
                    { value: 'user', label: 'Specific User' },
                  ]}
                  isRow
                />

                {firstApproverType === 'user' && (
                  <Select name="firstApprover" label="First Approver" options={userOptions} isRow />
                )}

                <Select name="secondApprover" label="Second Approver" options={userOptions} isRow isRequired={false} />
                <Select name="finalApprover" label="Third Approver" options={userOptions} isRow isRequired={false} />
              </Flex>
            </ModalBody>

            <ModalFooter>
              <HStack>
                <Button colorScheme="green" type="submit">
                  {isCreate ? 'Add' : 'Update'}
                </Button>

                <Button colorScheme="gray" onClick={closeModal}>
                  Close
                </Button>
              </HStack>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  )
}

export default ApplicationTypeDetailModal
